/**
 * Monaco-backed JS editor for watch transforms, with:
 * - ambient types for `input` / emission contract (IntelliSense)
 * - live `input.output` typing inferred from the selected tool's preview sample
 * - stateless map templates (count / thin-list / strip-jitter)
 * - LLM-assisted generation from a natural-language reshape rule
 * - drag-to-resize editor + mouse-wheel scroll
 */
import Editor, { type OnMount } from '@monaco-editor/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { parseJsonObjectText, parseTimeoutMs } from './json-object-field';
import {
  emptyTransformFields,
  transformPlaceholder,
  type ITransformFieldsValue,
} from './watch-wizard-shared';

const defaultEditorHeight = 320;
const minEditorHeight = 160;
const maxEditorHeight = 900;

/** Infer a TypeScript type string from a JSON-ish sample value. */
export function inferTsTypeFromValue(value: unknown, depth = 0): string {
  if (depth > 6) return 'unknown';
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  const t = typeof value;
  if (t === 'string') return 'string';
  if (t === 'number') return Number.isInteger(value) ? 'number' : 'number';
  if (t === 'boolean') return 'boolean';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'unknown[]';
    // Union a few element shapes so heterogeneous arrays still complete.
    const samples = value.slice(0, 8).map((v) => inferTsTypeFromValue(v, depth + 1));
    const unique = [...new Set(samples)];
    const first = unique[0] ?? 'unknown';
    const elem = unique.length === 1 ? first : `(${unique.join(' | ')})`;
    return `${elem}[]`;
  }
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).slice(0, 40);
    if (keys.length === 0) return 'Record<string, unknown>';
    const pad = '  '.repeat(depth + 1);
    const close = '  '.repeat(depth);
    const lines = keys.map((k) => {
      const safe = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(k) ? k : JSON.stringify(k);
      return `${pad}${safe}: ${inferTsTypeFromValue(obj[k], depth + 1)};`;
    });
    const more =
      Object.keys(obj).length > keys.length ? `\n${pad}// …truncated fields omitted` : '';
    return `{\n${lines.join('\n')}${more}\n${close}}`;
  }
  return 'unknown';
}

/**
 * Ambient .d.ts injected into Monaco. When `sampleOutput` is present (projected
 * tool result), `input.output` is typed from that sample so field completions
 * match the selected tool. Without a sample, output stays `unknown`.
 */
export function buildWatchTransformLibSource(opts: {
  toolName?: string;
  selector?: string;
  sampleOutput?: unknown;
  bindingKeys?: string[];
}): string {
  const toolLine = opts.toolName ? ` * Tool: \`${opts.toolName}\`` : ' * Tool: (none selected yet)';
  const selLine = opts.selector
    ? ` * Selector: \`${opts.selector}\` (input.output is AFTER projection)`
    : ' * Selector: (none — input.output is the full tool result)';
  const sampleNote =
    opts.sampleOutput !== undefined
      ? ' * Output type inferred from the latest Preview sample.'
      : ' * No Preview sample yet — run Preview above to type input.output from live data.';

  const outputType =
    opts.sampleOutput !== undefined
      ? inferTsTypeFromValue(opts.sampleOutput)
      : 'unknown /* Preview the tool to infer a concrete type */';

  const bindingDecls =
    opts.bindingKeys && opts.bindingKeys.length > 0
      ? opts.bindingKeys
          .filter((k) => /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(k))
          .map(
            (k) =>
              `/** Binding constant from the Bindings JSON (injected at runtime). */\ndeclare const ${k}: unknown;`,
          )
          .join('\n')
      : '';

  return `
/**
 * Watch map ambient types (auto-injected by the console).
${toolLine}
${selLine}
${sampleNote}
 *
 * Your body runs as a QuickJS fragment. Available:
 *   - input.output  → projected MCP tool result for this poll
 *   - binding keys  → top-level constants from the Bindings JSON
 *
 * Pure map: return the shaped payload for the fact lane.
 * return undefined to skip this poll. Host hash/condition decides when to wake.
 * There is NO input.prev and NO state.
 */
type WatchToolOutput = ${outputType};

interface WatchMapInput {
  /** Projected MCP tool result for this poll. */
  output: WatchToolOutput;
}

declare const input: WatchMapInput;

/** Return type: any JSON value, or undefined to skip. */
type WatchMapResult = unknown;

declare function __watchTransformReturn(result: WatchMapResult): WatchMapResult;

${bindingDecls}
`.trim();
}

/** Pretty block comment for the schema strip / insert-into-code action. */
export function formatInputSchemaComment(opts: {
  toolName?: string;
  selector?: string;
  sampleOutput?: unknown;
}): string {
  const header = [
    '/**',
    ' * input — available to this map every poll',
    opts.toolName ? ` * tool: ${opts.toolName}` : ' * tool: (not selected)',
    opts.selector ? ` * selector: ${opts.selector}` : ' * selector: (none — full tool result)',
    ' *',
    ' * input.output:',
  ];
  if (opts.sampleOutput === undefined) {
    header.push(
      ' *   unknown  // run Preview above to infer fields from a live sample',
      ' *',
      ' * return <payload> | undefined  // shaped fact value; undefined = skip poll',
      ' */',
    );
    return header.join('\n');
  }
  const typeBody = inferTsTypeFromValue(opts.sampleOutput);
  const indented = typeBody
    .split('\n')
    .map((line) => ` *   ${line}`)
    .join('\n');
  return [
    ...header,
    indented,
    ' *',
    ' * return <payload> | undefined  // shaped fact value; undefined = skip poll',
    ' */',
  ].join('\n');
}

export interface IWatchTransformTemplate {
  id: string;
  label: string;
  /** Shown under the chip on hover. */
  hint: string;
  code: string;
  /** Optional bindings JSON to seed alongside the template. */
  bindingsText?: string;
}

export const watchTransformTemplates: IWatchTransformTemplate[] = [
  {
    id: 'count-only',
    label: 'Count only',
    hint: 'Map a list to { count }. Pair with hash or condition gt 0 — no edge JS.',
    code: [
      '/** Map list → { count }. Host hash/condition owns when to wake. */',
      'const out = input.output;',
      'const items = Array.isArray(out) ? out : (out && (out.events || out.items || out.drones)) || [];',
      'return { count: items.length };',
    ].join('\n'),
  },
  {
    id: 'thin-list',
    label: 'Thin id list',
    hint: 'Keep only stable ids so hash ignores noisy fields.',
    code: [
      '/** Project open items to stable ids — strips timestamps/jitter from the hash. */',
      'const out = input.output || {};',
      'const items = out.events || out.alarms || out.items || (Array.isArray(out) ? out : []);',
      'const open = items.filter(function (e) {',
      '  return e && !e.resolvedAt && !e.expired && !e.done;',
      '});',
      'return open',
      '  .map(function (e) { return e.id || e.event_id || e.package_id; })',
      '  .filter(Boolean)',
      '  .sort();',
    ].join('\n'),
  },
  {
    id: 'strip-jitter',
    label: 'Strip jitter fields',
    hint: 'Drop timestamps / updatedAt so hash only sees real state.',
    code: [
      '/** Deep-ish strip of common volatile keys before hash. */',
      'function strip(v) {',
      '  if (Array.isArray(v)) return v.map(strip);',
      "  if (!v || typeof v !== 'object') return v;",
      '  var out = {};',
      '  Object.keys(v).forEach(function (k) {',
      "    if (k === 'updatedAt' || k === 'timestamp' || k === 'ts' || k === 'polledAt') return;",
      '    out[k] = strip(v[k]);',
      '  });',
      '  return out;',
      '}',
      'return strip(input.output);',
    ].join('\n'),
  },
  {
    id: 'battery-floor-list',
    label: 'Units below floor',
    hint: 'Return the list of units under bindings.floor. Use condition or hash on the result.',
    bindingsText: '{\n  "floor": 35\n}',
    code: [
      '/** Units currently below `floor` (binding). Pair with hash-on-change. */',
      'const out = input.output || {};',
      'const items = out.drones || out.units || out.items || [];',
      "const floor = typeof floor === 'number' ? floor : 35;",
      'return items',
      '  .filter(function (d) {',
      '    const bat = d.battery != null ? d.battery : d.battery_pct;',
      "    return typeof bat === 'number' && bat < floor;",
      '  })',
      '  .map(function (d) {',
      '    return {',
      '      id: d.drone_id || d.id,',
      '      battery: d.battery != null ? d.battery : d.battery_pct,',
      '    };',
      '  });',
    ].join('\n'),
  },
];

export interface ITransformEditorProps {
  value: ITransformFieldsValue;
  onChange: (patch: Partial<ITransformFieldsValue>) => void;
  /**
   * Optional live sample (projected tool output) — types `input.output` in
   * Monaco and is fed to the LLM generator. Pass the first preview sample.
   */
  sampleOutput?: unknown;
  /** Selector currently applied (prompt + ambient-type context). */
  selector?: string;
  /** Selected MCP tool name — shown in ambient types / schema strip. */
  toolName?: string;
  /**
   * Call the API to compile a natural-language wake rule into JS.
   * Parent owns the URL (thread vs group scope).
   */
  onGenerate?: (args: {
    prompt: string;
    sampleOutput?: unknown;
    selector?: string;
    bindings?: Record<string, unknown>;
  }) => Promise<{ code: string; rationale?: string }>;
  /** Initial Monaco height in px (user can drag-resize). */
  height?: number;
}

/**
 * Full transform editor: Monaco + templates + LLM assist + sample-typed input.
 */
export function TransformEditor({
  value,
  onChange,
  sampleOutput,
  selector,
  toolName,
  onGenerate,
  height: initialHeight = defaultEditorHeight,
}: ITransformEditorProps) {
  const bindingsError = parseJsonObjectText(value.bindingsText, 'Bindings').error;
  const bindingsParsed = parseJsonObjectText(value.bindingsText, 'Bindings');
  const bindingKeys = bindingsParsed.value ? Object.keys(bindingsParsed.value) : [];

  const [nlPrompt, setNlPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [rationale, setRationale] = useState<string | null>(null);
  const [schemaOpen, setSchemaOpen] = useState(true);
  const [editorHeight, setEditorHeight] = useState(initialHeight);

  const disposables = useRef<Array<{ dispose: () => void }>>([]);
  const libDisposable = useRef<{ dispose: () => void } | null>(null);
  const monacoRef = useRef<{
    languages: {
      typescript: {
        javascriptDefaults: { addExtraLib: (s: string, u: string) => { dispose: () => void } };
      };
    };
  } | null>(null);
  const editorRef = useRef<{ updateOptions: (o: Record<string, unknown>) => void } | null>(null);
  const resizeDrag = useRef<{ startY: number; startH: number } | null>(null);

  const bindingKeysKey = bindingKeys.join('\0');
  const libSource = useMemo(
    () =>
      buildWatchTransformLibSource({
        toolName,
        selector,
        sampleOutput,
        bindingKeys,
      }),
    // bindingKeysKey tracks bindings identity without depending on a new array each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolName, selector, sampleOutput, bindingKeysKey],
  );

  const schemaComment = useMemo(
    () =>
      formatInputSchemaComment({
        toolName,
        selector,
        sampleOutput,
      }),
    [toolName, selector, sampleOutput],
  );

  // Keep Monaco ambient types in sync when tool/sample/bindings change.
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco) return;
    libDisposable.current?.dispose();
    libDisposable.current = monaco.languages.typescript.javascriptDefaults.addExtraLib(
      libSource,
      'ts:watch-transform-ambient.d.ts',
    );
  }, [libSource]);

  useEffect(() => {
    return () => {
      for (const d of disposables.current) d.dispose();
      disposables.current = [];
      libDisposable.current?.dispose();
      libDisposable.current = null;
    };
  }, []);

  // Drag-resize: track pointer on the handle.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = resizeDrag.current;
      if (!drag) return;
      const next = Math.min(
        maxEditorHeight,
        Math.max(minEditorHeight, drag.startH + (e.clientY - drag.startY)),
      );
      setEditorHeight(next);
    };
    const onUp = () => {
      resizeDrag.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const handleMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      libDisposable.current?.dispose();
      libDisposable.current = monaco.languages.typescript.javascriptDefaults.addExtraLib(
        libSource,
        'ts:watch-transform-ambient.d.ts',
      );

      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES2020,
        allowNonTsExtensions: true,
        checkJs: true,
        module: monaco.languages.typescript.ModuleKind.None,
      });
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: false,
        noSyntaxValidation: false,
      });

      const completion = monaco.languages.registerCompletionItemProvider('javascript', {
        triggerCharacters: ['.', ' '],
        provideCompletionItems: (
          model: {
            getWordUntilPosition: (p: { lineNumber: number; column: number }) => {
              startColumn: number;
              endColumn: number;
            };
          },
          position: { lineNumber: number; column: number },
        ) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };
          const Kind = monaco.languages.CompletionItemKind;
          return {
            suggestions: [
              {
                label: 'input.output',
                kind: Kind.Variable,
                insertText: 'input.output',
                documentation:
                  'Projected MCP tool result for this poll (typed from Preview sample when available).',
                range,
              },
              {
                label: 'input.output',
                kind: Kind.Variable,
                insertText: 'input.output',
                documentation: 'Projected MCP tool result for this poll.',
                range,
              },
              {
                label: 'return mapped payload',
                kind: Kind.Snippet,
                insertText: [
                  '/** Pure map — host hash/condition decide when to wake. */',
                  'return ${1:{ count: (input.output && input.output.length) || 0 }};',
                ].join('\n'),
                documentation: 'Skeleton return of a shaped payload.',
                range,
              },
              {
                label: 'return undefined (skip poll)',
                kind: Kind.Snippet,
                insertText: 'return undefined; // skip this poll (no wake, no hash update)',
                documentation: 'Skip the poll entirely.',
                range,
              },
            ],
          };
        },
      });
      disposables.current.push(completion);

      editor.updateOptions({
        minimap: { enabled: false },
        fontSize: 12,
        lineNumbers: 'on',
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        automaticLayout: true,
        tabSize: 2,
        // Capture wheel so long transforms scroll inside the editor.
        scrollbar: {
          alwaysConsumeMouseWheel: true,
          vertical: 'auto',
          horizontal: 'auto',
          verticalScrollbarSize: 10,
          horizontalScrollbarSize: 10,
        },
        suggestOnTriggerCharacters: true,
        quickSuggestions: true,
        snippetSuggestions: 'inline',
        mouseWheelZoom: false,
      });
    },
    // libSource is re-applied via the dedicated useEffect; mount uses latest at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const applyTemplate = (tpl: IWatchTransformTemplate) => {
    // Prepend a live schema comment when we have a sample so the buffer itself
    // documents the shape of input.output for this tool.
    const prefix =
      sampleOutput !== undefined || toolName
        ? `${formatInputSchemaComment({ toolName, selector, sampleOutput })}\n\n`
        : '';
    onChange({
      code: prefix + tpl.code,
      ...(tpl.bindingsText != null ? { bindingsText: tpl.bindingsText } : {}),
    });
    setRationale(`Template: ${tpl.label}`);
    setGenError(null);
  };

  const insertSchemaComment = () => {
    const block = formatInputSchemaComment({ toolName, selector, sampleOutput });
    const existing = value.code;
    // Avoid double-inserting the same header.
    if (existing.startsWith('/**\n * input — available')) {
      onChange({ code: `${block}\n${existing.replace(/^\/\*\*[\s\S]*?\*\/\n?/, '')}` });
    } else {
      onChange({ code: existing.trim() ? `${block}\n\n${existing}` : `${block}\n` });
    }
  };

  const runGenerate = async () => {
    if (!onGenerate) return;
    const prompt = nlPrompt.trim();
    if (!prompt) {
      setGenError('Describe what should wake the agent first.');
      return;
    }
    if (bindingsParsed.error) {
      setGenError(bindingsParsed.error);
      return;
    }
    setGenerating(true);
    setGenError(null);
    setRationale(null);
    try {
      const out = await onGenerate({
        prompt,
        sampleOutput,
        selector: selector || undefined,
        bindings: bindingsParsed.value,
      });
      const prefix =
        sampleOutput !== undefined || toolName
          ? `${formatInputSchemaComment({ toolName, selector, sampleOutput })}\n\n`
          : '';
      onChange({ code: prefix + out.code });
      setRationale(out.rationale ?? 'Generated from your wake rule.');
    } catch (err) {
      setGenError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  };

  const sampleStatus =
    sampleOutput !== undefined
      ? 'typed from Preview sample'
      : toolName
        ? 'tool selected — Preview to type fields'
        : 'select a tool + Preview to type input.output';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          borderLeft: '3px solid var(--accent-primary, #6ea8ff)',
          background: 'var(--surface-1, rgba(255,255,255,0.02))',
          padding: 'var(--sp-2) var(--sp-3)',
          borderRadius: 'var(--radius-sm, 4px)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-secondary)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div>
          <code>input.output</code> — tool result <strong>after your selector</strong>. Pure map
          only — no <code>prev</code>, no state, no emissions protocol.
        </div>
        <div>
          <code>return</code> the shaped payload for the fact lane, or <code>undefined</code> to
          skip the poll. Host hash/condition decide when to wake. QuickJS sandbox — no
          network/fs/Node. Monaco types <code>input.output</code> from your Preview sample (
          {sampleStatus}).
        </div>
      </div>

      {/* Live input schema strip (also injected into Monaco IntelliSense) */}
      <div
        style={{
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm, 4px)',
          background: 'var(--surface-1, rgba(255,255,255,0.02))',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 8px',
            borderBottom: schemaOpen ? '1px solid var(--border-subtle)' : 'none',
          }}
        >
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-expanded={schemaOpen}
            onClick={() => setSchemaOpen((v) => !v)}
          >
            {schemaOpen ? '▾' : '▸'} input TypeScript schema
          </button>
          <span className="t-xs t-muted" style={{ flex: 1 }}>
            {toolName ? <code>{toolName}</code> : 'no tool'} · {sampleStatus}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            title="Prepend this schema as a block comment in the editor"
            onClick={insertSchemaComment}
          >
            Insert into code
          </button>
        </div>
        {schemaOpen && (
          <pre
            className="t-mono t-xs"
            style={{
              margin: 0,
              padding: '8px 10px',
              maxHeight: 180,
              overflow: 'auto',
              whiteSpace: 'pre',
              color: 'var(--text-secondary)',
            }}
          >
            {schemaComment}
          </pre>
        )}
      </div>

      {/* Templates */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span className="t-xs t-muted">Templates:</span>
        {watchTransformTemplates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            className="btn btn--ghost btn--sm"
            title={tpl.hint}
            onClick={() => applyTemplate(tpl)}
          >
            {tpl.label}
          </button>
        ))}
        {value.code.trim() && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              onChange({ ...emptyTransformFields, timeoutMs: value.timeoutMs });
              setRationale(null);
            }}
          >
            Clear → hash mode
          </button>
        )}
      </div>

      {/* LLM assist */}
      {onGenerate && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: 'var(--sp-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm, 4px)',
            background: 'var(--surface-1, rgba(255,255,255,0.02))',
          }}
        >
          <span className="field-label">Generate with AI</span>
          <span className="t-xs t-muted">
            Describe the wake rule in plain English. Uses an org OpenAI model when one exists (else
            the base agent model). Pass a live Preview sample first so the model sees real field
            names
            {sampleOutput === undefined ? ' (no sample yet — Preview the tool above).' : '.'}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <textarea
              className="input t-sm"
              rows={2}
              value={nlPrompt}
              onChange={(e) => setNlPrompt(e.target.value)}
              placeholder={
                'e.g. Wake me only when a drone newly drops below 30% battery; one emission per drone id'
              }
              style={{ flex: 1, resize: 'vertical' }}
              disabled={generating}
            />
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                runGenerate().catch(() => undefined);
              }}
              disabled={generating || !nlPrompt.trim()}
              style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
            >
              {generating ? 'Generating…' : 'Generate JS'}
            </button>
          </div>
          {genError && (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {genError}
            </span>
          )}
          {rationale && !genError && <span className="t-xs t-muted">{rationale}</span>}
        </div>
      )}

      <div className="field" style={{ margin: 0 }}>
        <span className="field-label">Transform (JavaScript) — empty = hash-on-change</span>
        <div
          style={{
            border: '1px solid var(--border-subtle)',
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: '#1e1e1e',
          }}
        >
          <div style={{ height: editorHeight, minHeight: minEditorHeight }}>
            <Editor
              height="100%"
              defaultLanguage="javascript"
              language="javascript"
              theme="vs-dark"
              value={value.code}
              onChange={(v) => onChange({ code: v ?? '' })}
              onMount={handleMount}
              options={{
                ariaLabel: 'Watch transform JavaScript',
              }}
              loading={<span className="t-xs t-muted">Loading editor…</span>}
            />
          </div>
          {/* Drag handle — vertical resize */}
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize transform editor"
            title="Drag to resize"
            onPointerDown={(e) => {
              e.preventDefault();
              (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
              resizeDrag.current = { startY: e.clientY, startH: editorHeight };
            }}
            style={{
              height: 8,
              cursor: 'ns-resize',
              background:
                'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.06) 40%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 60%, transparent 100%)',
              borderTop: '1px solid var(--border-subtle)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              touchAction: 'none',
            }}
          >
            <span
              aria-hidden={true}
              style={{
                width: 36,
                height: 3,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.28)',
              }}
            />
          </div>
        </div>
        {!value.code.trim() && (
          <pre
            className="t-mono t-xs t-muted"
            style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}
          >
            {transformPlaceholder}
          </pre>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label className="field" style={{ flex: 1, minWidth: 220 }}>
          <span className="field-label">Bindings (optional JSON object — top-level constants)</span>
          <textarea
            className="input t-mono"
            rows={2}
            value={value.bindingsText}
            placeholder='{"floor": 35}'
            spellCheck={false}
            onChange={(e) => onChange({ bindingsText: e.target.value })}
            style={{ resize: 'vertical' }}
          />
          {bindingsError && (
            <span className="field-help" style={{ color: 'var(--status-danger)' }}>
              {bindingsError}
            </span>
          )}
        </label>
        <label className="field" style={{ flex: '0 0 150px' }}>
          <span className="field-label">Timeout (ms)</span>
          <input
            className="input"
            type="number"
            min={100}
            max={30000}
            step={100}
            value={value.timeoutMs}
            onChange={(e) => onChange({ timeoutMs: e.target.value })}
          />
          {parseTimeoutMs(value.timeoutMs).error && (
            <span className="field-help" style={{ color: 'var(--status-danger)' }}>
              {parseTimeoutMs(value.timeoutMs).error}
            </span>
          )}
        </label>
      </div>
    </div>
  );
}

// Re-export for callers that only need the type/helpers from here.
export type { ITransformFieldsValue };
export { emptyTransformFields, transformPlaceholder };
